import React, { useCallback, useState } from 'react';
import { useFetchApiData, useIsBrl } from '../../../utils/hooks';
import { useAppContext } from '../../../context/AppContext';
import { getLoansByUnit, getLoansForClient } from '../../../api';
import { useForegroundLoader } from '../../../context/LoadingContext';
import LoanCard from './LoanCard';
import Greeting from '../../../components/Modules/Greeting';
import ResidenceSelector from '../../../components/Modules/Residence/ResidenceSelector';
import { BateIcon } from '../../../types/enums';
import RoundedBox from '../../../components/Containers/RoundedBox';
import { formatCurrency } from '../../../utils/numbers';
import { BATE_RED_HEADING_STYLE } from '../../../common/utils/constants';
import { cn } from '../../../utils/style';

export const LoanPage: React.FC = () => {
  const { loadingResidenceList, currentResidence } = useAppContext();

  const [errorLoadingLoans, setErrorLoadingLoans] = useState(false);
  const [errorLoadingUnitLoans, setErrorLoadingUnitLoans] = useState(false);

  const isBrl = useIsBrl(currentResidence);

  const fetchLoans = useCallback(async () => {
    if (currentResidence?.clientId != null) {
      try {
        return getLoansForClient(currentResidence?.clientId);
      } catch (error) {
        setErrorLoadingLoans(true);
        console.error(error);
      }
    }
    return null;
  }, [currentResidence]);

  const fetchUnitLoans = useCallback(async () => {
    if (currentResidence?.id != null) {
      try {
        return getLoansByUnit(currentResidence.id);
      } catch (error) {
        setErrorLoadingUnitLoans(true);
        console.error(error);
      }
    }
    return null;
  }, [currentResidence]);

  const {
    data: loans,
    loading: loadingLoans,
    refetch: refetchLoans,
  } = useFetchApiData({ fetch: fetchLoans, identifier: 'LoanPage:loans' });
  const {
    data: unitLoans,
    loading: loadingUnitLoans,
    refetch: refetchUnitLoans,
  } = useFetchApiData({ fetch: fetchUnitLoans, identifier: 'LoanPage:unitLoans' });

  useForegroundLoader([loadingResidenceList, loadingUnitLoans, loadingLoans]);

  const hasInLoan = loans?.some((loan) => loan.IsIn);

  return (
    <div className="bate-container mt-8 gap-8 flex flex-col">
      <div>
        <Greeting
          title={`Lån for ${currentResidence?.address}`}
          icon={BateIcon.Bonus}
        />
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        <div className="md:row-span-2 order-1 md:order-2 lg:order-3">
          <ResidenceSelector />
        </div>

        <RoundedBox
          className={cn([
            'mb-4 flex flex-col gap-4 h-full order-2 md:order-1',
            hasInLoan ? 'justify-between' : 'lg:col-span-2',
          ])}
        >
          <h3 className={BATE_RED_HEADING_STYLE}>Andel fellesgjeld</h3>
          <span className="font-clanot-ultra text-3xl">
            {loadingUnitLoans
              ? '-'
              : formatCurrency(unitLoans?.reduce((sum, current) => sum + current.loanAmount, 0))}
          </span>
        </RoundedBox>
        <RoundedBox
          className={cn([
            'flex flex-col gap-4 h-full order-3',
            hasInLoan ? 'lg:order-2 justify-between' : 'lg:col-span-2',
          ])}
        >
          <h3 className={BATE_RED_HEADING_STYLE}>{`Fellesgjeld for ${currentResidence?.clientName}`}</h3>
          <span className="font-clanot-ultra text-3xl">
            {loadingLoans
              ? '-'
              : formatCurrency(loans?.reduce((sum, current) => sum + current.loanBalance, 0))}
          </span>
        </RoundedBox>

        <RoundedBox className={`flex gap-4 flex-col md:col-span-2 order-4 ${hasInLoan ? '' : 'hidden'}`}>
          <h3 className={BATE_RED_HEADING_STYLE}>Du har IN-ordning</h3>
          <p className="mb-4">
            IN (individuell nedbetaling) betyr at du kan betale ned fellesgjelden din
            - helt eller delvis - og få lavere felleskostnader.
          </p>
          <a
            href="https://bate.no/for-deg/hva-er-in-ordning"
            className="text-bate-red underline"
          >
            Her kan du lese mer om ordningen.
          </a>
          <a
            href="https://bate.no/for-deg/hva-er-in-ordning#kontakt"
            className="text-bate-red underline"
          >
            Kontakt oss hvis du vil betale ned.
          </a>
        </RoundedBox>

      </div>

      <div className="flex flex-col gap-4">
        {errorLoadingLoans
          && (
            <div>
              <span>Kunne ikke hente lån.</span>
              <button
                type="button"
                className="mx-2 text-bate-red underline"
                onClick={() => {
                  setErrorLoadingLoans(false);
                  refetchLoans();
                }}
              >
                Prøv igjen.
              </button>
              <span>Hvis problemet fortsetter, kontakt kundeservice.</span>
            </div>
          )}
        {errorLoadingUnitLoans
          && (
            <div>
              <span>Kunne ikke hente lån andel for din enhet.</span>
              <button
                type="button"
                className="mx-2 text-bate-red underline"
                onClick={() => {
                  setErrorLoadingUnitLoans(false);
                  refetchUnitLoans();
                }}
              >
                Prøv igjen.
              </button>
              <span>Hvis problemet fortsetter, kontakt kundeservice.</span>
            </div>
          )}
        {loans?.map((loan) => {
          const unitLoan = unitLoans?.find((ul) => ul.id === loan.id);
          return (
            <LoanCard key={loan.id} loan={loan} unitLoan={unitLoan} />
          );
        })}
      </div>

      <RoundedBox>
        <h3 className={BATE_RED_HEADING_STYLE}>Hva er fellesgjeld?</h3>
        <p className="mt-4">
          {
            isBrl
              ? 'De fleste borettslag har fellesgjeld. Det er lån som borettslaget har tatt opp for å bygge boligene '
                + '– eller for å betale for oppussing og vedlikehold. '
                + 'Du betaler din andel av fellesgjelden gjennom felleskostnadene hver måned.'
              : 'Fellesgjeld er lån sameiet har tatt opp – for eksempel til vedlikehold eller oppgraderinger. '
                + 'Hvis sameiet ditt har lån, betaler du renter og avdrag via felleskostnadene hver måned.'
          }
        </p>
      </RoundedBox>
    </div>
  );
};
